/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useEffect,
  useState,
  useCallback,
  ChangeEvent,
  useRef,
} from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import {
  FaLink,
  FaEllipsisH,
  FaUserPlus,
  FaArrowCircleRight,
  FaSync,
  FaFingerprint,
  FaShieldAlt,
  FaUserAlt,
  FaCheck,
  FaSortUp,
  FaSortDown,
  FaSort,
} from "react-icons/fa";
import { MdMergeType } from "react-icons/md";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Tooltip,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import { ApiClient } from "../helpers/apiClient";
import { useEventContext } from "../contexts/EventContext";
import { useProfileContext } from "../contexts/ProfileContext";
import { loadParticipantsForEvent } from "../utils/participantsUtils";
import { TeamDTO } from "../types/teamMembers";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { EventPhase } from "../enums/EventsPhaseEnum";
import {
  EVENT_STATUS_LABELS,
  EventStatusFilter,
  GRADING_STATUSES,
  INTERVIEW_STATUSES,
  PHASE_STATUS_VISIBILITY,
  TEST_STATUSES,
} from "../enums/EventStatusFilter";
import { ParticipantStatus } from "../enums/ParticipantStatusEnum";
import { InterviewStatus } from "../enums/InterviewStatusEnum";
import { MergeModeEnum } from "../enums/MergeModeEnum";

export type ParticipantDTO = {
  id: string;
  email: string;
  fullName: string;
  deviceFingerPrint: string | null;
  code: string | null;
  linkKey: string;
  hasRemarks: boolean;
  abandoned: boolean;
  lastSubmission: string | null;
  finishedAt: string | null;
  url: string;
  status: ParticipantStatus;
  interviewStatus: InterviewStatus;
  location: string;
  faculty: string;
  lastLogin: string | null;
  team: TeamDTO;
  gradingStatusDTOs: GradingStatusTeamMemberDTO[];
  merged: boolean;
  points: number;
  phone: string | null;
  yearOfStudy: string | null;
  date: string | null;
};

type MenuItemType = {
  label: string;
  action?: number;
  locationId?: number;
  teamId?: number;
  children?: MenuItemType[];
};

type GradingStatusPerGroupDTO = {
  gradingGroupName: string;
  gradingGroupID: number;
  isCorrected: boolean;
};

type GradingStatusTeamMemberDTO = {
  teamMemberName?: string;
  teamMemberID: number;
  gradingStatus: GradingStatus;
  gradingStatusPerGradingDetailsDTOs: GradingStatusPerGroupDTO[];
};

enum GradingStatus {
  NotStarted = 0,
  InProgress = 1,
  Finished = 2,
}

type EventDTO = {
  id: string;
  eventID: string;
  name: string;
  enabled: boolean;
  locationId?: number;
  locationName?: string;
  allLocations?: { id: number; name: string }[];
  status: number;
  phase: EventPhase;
  teams: TeamDTO[];
};

const FONT_STYLE = { fontFamily: "Verdana", fontSize: "13px" };

const interviewStatusColorMap: Record<InterviewStatus, string> = {
  [InterviewStatus.NotStarted]: "gray",
  [InterviewStatus.DoneWithCorrection]: "orange",
  [InterviewStatus.Done]: "#1d62bf",
};

enum MenuOps {
  AddParticipant,
  Logout,
  CloseAllInProgressSessions,
  AssignTeams,
  AddTeamMembers,
  AcceptParticipants,
}

const Dashboard: React.FC = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const { profile } = useProfileContext();
  const { selectedEventId, events } = useEventContext();

  const [participants, setParticipants] = useState<ParticipantDTO[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<EventDTO | undefined>();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<EventStatusFilter>();
  const [teamFilter, setTeamFilter] = useState<string>("ALL");
  const [selectedParticipantId, setSelectedParticipantId] = useState<
    string | null
  >(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<"points" | "merged" | "date" | null>(
    null,
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [assignTeamDialogOpen, setAssignTeamDialogOpen] = useState(false);
  const [addTeamMembersDialogOpen, setAddTeamMembersDialogOpen] =
    useState(false);
  const [acceptParticipantsDialogOpen, setAcceptParticipantsDialogOpen] =
    useState(false);
  const [minPoints, setMinPoints] = useState<number | null>(null);
  const [teamMembersFile, setTeamMembersFile] = useState<File | null>(null);
  const [locationFilter, setLocationFilter] = useState<string>("ALL");
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(
    null,
  );
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [subMenuItems, setSubMenuItems] = useState<MenuItemType[]>([]);
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tableWrapperRef.current &&
        !tableWrapperRef.current.contains(event.target as Node)
      ) {
        setActiveRowId(null);
        closeAllMenus();
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setSearchTerm("");
    setTeamFilter("ALL");
    setLocationFilter("ALL");
    setSortBy(null);
    setSortOrder("desc");
    setPage(0);

    var newStatusFilter = EventStatusFilter.ALL;
    if (currentEvent?.phase === EventPhase.Grading) {
      newStatusFilter = EventStatusFilter.GRADING_ALL;
    } else if (currentEvent?.phase === EventPhase.Interview) {
      newStatusFilter = EventStatusFilter.INTERVIEW_ALL;
    }
    setStatusFilter(newStatusFilter);
    setParticipants([]);

    refreshParticipants(newStatusFilter);
  }, [selectedEventId]);

  useEffect(() => {
    const apiClient = ApiClient.getInstance(instance, accounts[0], {
      baseUrl: process.env.REACT_APP_API_BASE_URL,
    });
    const connection = new HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_API_BASE_URL}/notificationHub`, {
        accessTokenFactory: () => apiClient.getAccessToken(),
      })
      .withAutomaticReconnect()
      .build();

    const startConnection = async () => {
      try {
        await connection.start();
        console.log("Connected to NotificationHub");

        connection.on(
          "ParticipantUpdate",
          (updatedParticipant: ParticipantDTO) => {
            setParticipants((prevParticipants) => {
              const index = prevParticipants.findIndex(
                (p) => p.id === updatedParticipant.id,
              );
              const newParticipants = [...prevParticipants];
              newParticipants[index] = updatedParticipant;
              return newParticipants;
            });
          },
        );
      } catch (err) {
        console.error("SignalR error:", err);
      }
    };

    startConnection();

    return () => {
      connection.off("ParticipantUpdate");
      connection.stop();
    };
  }, []);

  const currentEvent: EventDTO | undefined = events.find(
    (ev) => ev.id === selectedEventId,
  );
  const hasMultipleLocations =
    (currentEvent?.allLocations?.length ?? 0) > 1 &&
    currentEvent?.phase === EventPhase.Test;

  const refreshParticipants = useCallback(
    async (newStatusFilter?: EventStatusFilter) => {
      if (!selectedEventId) {
        setParticipants([]);
        return;
      }
      try {
        setPage(0);
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
        const list = await loadParticipantsForEvent(
          instance,
          accounts[0],
          apiBaseUrl,
          currentEvent as any,
          newStatusFilter ?? statusFilter,
        );
        setParticipants(list);
        document.title = "CodeLab Admin";
        setSelectedEvent(currentEvent);
      } catch (err) {
        console.error("Error loading participants:", err);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [selectedEventId, currentEvent, statusFilter],
  );

  useEffect(() => {
    if (!currentEvent || !profile) return;

    const userTeam = currentEvent.teams.find(
      (team) =>
        team.teamMembers.some((tm) => tm.id === profile.id) &&
        team.eventPhase === currentEvent.phase,
    );

    if (userTeam) {
      setTeamFilter(String(userTeam.id));
    } else {
      setTeamFilter("ALL");
    }
  }, [currentEvent, profile]);

  const getMenuItemsForParticipant = (
    currentStatus: ParticipantStatus,
  ): MenuItemType[] => {
    switch (currentStatus) {
      case ParticipantStatus.Inactive: {
        if (hasMultipleLocations && currentEvent?.allLocations) {
          const currentLocName = selectedParticipant?.location ?? null;
          const locationChoices = currentEvent.allLocations
            .filter((loc) => loc.name !== currentLocName)
            .map((loc) => ({
              label: `Set as confirmed (${loc.name})`,
              action: 1,
              locationId: loc.id,
            }));
          return [...locationChoices, { label: "Set as abandoned", action: 4 }];
        }
        return [{ label: "Set as abandoned", action: 4 }];
      }
      case ParticipantStatus.InProgress:
        return [{ label: "Set as finished", action: 3 }];
      case ParticipantStatus.Finished: {
        if (selectedEvent?.phase === EventPhase.Test)
          return [{ label: "Set as in progress", action: 2 }];
        if (selectedEvent?.phase === EventPhase.Grading) {
          const items: MenuItemType[] = [];

          items.push({ label: "Start merge", action: 6 });

          const hasAnyCorrected = selectedParticipant?.gradingStatusDTOs?.some(
            (gs) =>
              gs.gradingStatusPerGradingDetailsDTOs?.some((d) => d.isCorrected),
          );

          if (profile?.isAdmin && !hasAnyCorrected) {
            items.push({
              label: "Assign",
              children:
                currentEvent?.teams
                  .filter((t) => t.eventPhase === EventPhase.Grading)
                  .map((team: any) => ({
                    label: team.nameWithMemberNames,
                    action: 8,
                    teamId: team.id,
                  })) ?? [],
            });
          }

          if (profile?.superAdmin) {
            items.push({ label: "Switch accept status", action: 9 });
          }

          return items;
        }

        return [];
      }
      case ParticipantStatus.Interview: {
        const isUserInTeam = selectedParticipant?.team?.teamMembers?.some(
          (tm) => tm.id === profile?.id,
        );

        const items: MenuItemType[] = [];

        if (selectedEvent?.phase === EventPhase.Interview && isUserInTeam) {
          items.push({ label: "Start interview", action: 7 });
        }

        if (profile?.superAdmin) {
          items.push({ label: "Switch accept status", action: 9 });
        }

        if (
          profile?.isAdmin &&
          selectedParticipant?.interviewStatus === InterviewStatus.NotStarted
        ) {
          items.push({
            label: "Assign",
            children:
              currentEvent?.teams
                .filter((t) => t.eventPhase === EventPhase.Interview)
                .map((team: any) => ({
                  label: team.nameWithMemberNames,
                  action: 8,
                  teamId: team.id,
                })) ?? [],
          });
        }

        return items;
      }

      case ParticipantStatus.Abandoned:
        if (currentEvent?.phase !== EventPhase.Interview) {
          if (hasMultipleLocations && currentEvent?.allLocations) {
            return currentEvent.allLocations.map((loc) => ({
              label: `Set as confirmed (${loc.name})`,
              action: 1,
              locationId: loc.id,
            }));
          }
          return [{ label: "Set as confirmed", action: 1 }];
        }
        return [];

      case ParticipantStatus.Accepted: {
        if (profile?.superAdmin) {
          return [{ label: "Switch accept status", action: 9 }];
        }
        return [];
      }

      default:
        return [];
    }
  };

  const selectedParticipant = participants.find(
    (p) => p.id === selectedParticipantId,
  );
  const possibleMenuItems = selectedParticipant
    ? getMenuItemsForParticipant(selectedParticipant.status)
    : [];

  const performParticipantAction = async (
    participantId: string,
    action: number,
    locationId?: number | null,
  ) => {
    const apiClient = ApiClient.getInstance(instance, accounts[0], {
      baseUrl: process.env.REACT_APP_API_BASE_URL,
    });
    await apiClient?.post(
      "Participant/PerformParticipantAction",
      JSON.stringify({ participantId, action, locationId: locationId ?? null }),
    );
  };

  const activateRow = (participantId: string) => {
    setActiveRowId(participantId);
  };

  const handlePerformAction = async (
    participantId: string,
    action: number,
    locationId?: number,
    teamId?: number,
  ) => {
    try {
      if (action === 6) {
        const linkKey =
          participants.find((p) => p.id === participantId)?.linkKey ?? "";
        window.open(
          `/code-viewer?linkKey=${encodeURIComponent(linkKey)}&merge=${MergeModeEnum.Merge}`,
          "_blank",
        );

        return;
      }

      if (action === 7) {
        const linkKey =
          participants.find((p) => p.id === participantId)?.linkKey ?? "";
        window.open(
          `/code-viewer?linkKey=${encodeURIComponent(linkKey)}&merge=${MergeModeEnum.Interview}`,
          "_blank",
        );

        return;
      }

      if (action === 8) {
        const apiClient = ApiClient.getInstance(instance, accounts[0], {
          baseUrl: process.env.REACT_APP_API_BASE_URL,
        });

        await apiClient.post(
          "Team/AssignTeamToParticipant",
          JSON.stringify({
            participantId,
            teamId: teamId,
          }),
        );

        refreshParticipants();
        return;
      }

      if (action === 9) {
        const apiClient = ApiClient.getInstance(instance, accounts[0], {
          baseUrl: process.env.REACT_APP_API_BASE_URL,
        });

        await apiClient.post(
          `Grading/SwitchParticipantStatus/${participantId}/${currentEvent?.eventID}`,
        );

        return;
      }

      await performParticipantAction(participantId, action, locationId);

      setParticipants((prev) => {
        if (locationId && locationId !== currentEvent?.locationId) {
          return prev.filter((p) => p.id !== participantId);
        }

        const updated = [...prev];
        const idx = updated.findIndex((x) => x.id === participantId);
        if (idx !== -1) {
          const item = { ...updated[idx] };
          if (action < 5) {
            //status changes
            item.status = action;
            item.finishedAt = action === 3 ? new Date().toISOString() : null;
            item.abandoned = action === 4;

            if (locationId) {
              const locName = currentEvent?.allLocations?.find(
                (l) => l.id === locationId,
              )?.name;
              if (locName) item.location = locName;
            }
          } else if (action === 5) {
            item.deviceFingerPrint = null;
          }
          updated.splice(idx, 1);
          updated.unshift(item);
        }
        return updated;
      });

      setPage(0);
    } catch (err) {
      console.error("Error performing participant action:", err);
    } finally {
      handleMenuClose();
    }
  };

  const handleMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    participantId: string,
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedParticipantId(participantId);
    setActiveRowId(participantId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedParticipantId(null);
  };

  const handleAssignTeams = async () => {
    const apiClient = ApiClient.getInstance(instance, accounts[0], {
      baseUrl: process.env.REACT_APP_API_BASE_URL,
    });
    await apiClient?.post(
      `Team/AssignTeamsToParticipants/${currentEvent?.eventID}`,
      "",
    );
    setAssignTeamDialogOpen(false);
    refreshParticipants();
  };

  const handleAddTeamMembers = async () => {
    if (!teamMembersFile) return;
    const text = await teamMembersFile.text();
    const teamMembers = JSON.parse(text);
    const apiClient = ApiClient.getInstance(instance, accounts[0], {
      baseUrl: process.env.REACT_APP_API_BASE_URL,
    });
    await apiClient?.post(
      `Team/AddTeamMembers/${currentEvent?.eventID}`,
      JSON.stringify(teamMembers),
    );
    setTeamMembersFile(null);
    setAddTeamMembersDialogOpen(false);
    refreshParticipants();
  };

  const handleAcceptParticipants = async () => {
    const apiClient = ApiClient.getInstance(instance, accounts[0], {
      baseUrl: process.env.REACT_APP_API_BASE_URL,
    });
    await apiClient?.post(
      `Grading/AcceptParticipants/${currentEvent?.eventID}/${minPoints}/${selectedLocationId}`,
      "",
    );
    setAcceptParticipantsDialogOpen(false);
    refreshParticipants();
  };

  const handleChangePage = (_evt: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (
    evt: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(evt.target.value, 10));
    setPage(0);
  };
  const onSearchTermChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };
  const onStatusFilterChange = (e: any) => {
    setStatusFilter(e.target.value as EventStatusFilter);
    refreshParticipants(e.target.value as EventStatusFilter);
    setPage(0);
  };
  const onTeamFilterChange = (e: any) => {
    setTeamFilter(e.target.value);
    setPage(0);
  };

  const onLocationFilterChange = (e: any) => {
    setLocationFilter(e.target.value);
    setPage(0);
  };

  const handleSortColumn = (column: "points" | "merged" | "date") => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "desc" ? "asc" : "desc");
      if (sortOrder === "desc") {
        setSortOrder("asc");
      } else {
        setSortBy(null);
        setSortOrder("desc");
      }
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
    setPage(0);
  };

  const getSortIcon = (column: "points" | "merged" | "date") => {
    if (sortBy !== column)
      return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    return sortOrder === "desc" ? (
      <FaSortDown style={{ marginLeft: 4 }} />
    ) : (
      <FaSortUp style={{ marginLeft: 4 }} />
    );
  };

  const handleParticipantLinkClick = (linkKey: string) => {
    window.open(
      `/code-viewer?linkKey=${encodeURIComponent(linkKey)}`,
      "_blank",
    );
  };

  const handleMergeCompletionClick = (linkKey: string) => {
    window.open(
      `/code-viewer?linkKey=${encodeURIComponent(linkKey)}&merge=${MergeModeEnum.MergeResult}`,
      "_blank",
    );
  };

  const performCloseAllInProgressSessions = async (
    eventID: string | undefined,
  ) => {
    const apiClient = ApiClient.getInstance(instance, accounts[0], {
      baseUrl: process.env.REACT_APP_API_BASE_URL,
    });
    await apiClient?.post(
      "Participant/CloseAllInProgressSessions",
      JSON.stringify({ eventID }),
    );
    refreshParticipants();
  };

  const handleSpeedDial = (op: MenuOps) => {
    if (op === MenuOps.AddParticipant) navigate("/addParticipant");
    if (op === MenuOps.Logout) instance.logoutPopup();
    if (op === MenuOps.CloseAllInProgressSessions)
      performCloseAllInProgressSessions(currentEvent?.eventID);
    if (op === MenuOps.AssignTeams) setAssignTeamDialogOpen(true);
    if (op === MenuOps.AddTeamMembers) setAddTeamMembersDialogOpen(true);
    if (op === MenuOps.AcceptParticipants) {
      handleOpenAcceptParticipantsDialog();
    }
  };

  const handleOpenAcceptParticipantsDialog = () => {
    if (currentEvent?.allLocations?.length === 1) {
      setSelectedLocationId(currentEvent.allLocations[0].id);
    } else {
      setSelectedLocationId(null);
    }

    setAcceptParticipantsDialogOpen(true);
  };

  const gradingStatusColorMap: Record<GradingStatus, string> = {
    [GradingStatus.Finished]: "#1d62bf",
    [GradingStatus.InProgress]: "orange",
    [GradingStatus.NotStarted]: "gray",
  };

  const actions = [
    profile?.isAdmin && {
      icon: <FaUserPlus />,
      name: "Add Participant",
      op: MenuOps.AddParticipant,
    },
    profile?.isAdmin &&
      currentEvent?.status === 3 &&
      participants.filter((p) => p.status === ParticipantStatus.InProgress)
        .length > 0 && {
        icon: <FaShieldAlt />,
        name: "Close all in-progress sessions",
        op: MenuOps.CloseAllInProgressSessions,
      },
    profile?.isAdmin &&
      currentEvent?.phase === EventPhase.Grading && {
        icon: <FaUserAlt />,
        name: "Assign Teams to Participants",
        op: MenuOps.AssignTeams,
      },
    profile?.isAdmin && {
      icon: <MdMergeType />,
      name: "Add Team Members",
      op: MenuOps.AddTeamMembers,
    },
    profile?.isAdmin &&
      (currentEvent?.phase === EventPhase.Grading ||
        currentEvent?.phase === EventPhase.Interview) && {
        icon: <FaCheck />,
        name: "Accept Participants",
        op: MenuOps.AcceptParticipants,
      },
  ].filter(Boolean) as { icon: any; name: string; op: MenuOps }[];

  const getStatusInfo = (
    status: ParticipantStatus,
  ): { label: string; color: string } => {
    switch (status) {
      case ParticipantStatus.Inactive:
        return { label: "Inactive", color: "gray" };
      case ParticipantStatus.InProgress:
        return { label: "In Progress", color: "goldenrod" };
      case ParticipantStatus.Finished:
        return { label: "Finished", color: "lightBlue" };
      case ParticipantStatus.Abandoned:
        return { label: "Abandoned", color: "pink" };
      case ParticipantStatus.Interview:
        return { label: "Interview", color: "orange" };
      case ParticipantStatus.Accepted:
        return { label: "Accepted", color: "lightGreen" };
      default:
        return { label: "None", color: "lightgray" };
    }
  };

  const getAllowedPhases = (
    statusFilter: EventStatusFilter | undefined,
  ): EventPhase[] => {
    if (statusFilter) {
      if (TEST_STATUSES.includes(statusFilter)) {
        return [EventPhase.Test];
      }

      if (GRADING_STATUSES.includes(statusFilter)) {
        return [EventPhase.Grading];
      }

      if (INTERVIEW_STATUSES.includes(statusFilter)) {
        return [EventPhase.Interview];
      }
    }

    if (statusFilter === EventStatusFilter.ALL) {
      return currentEvent ? [currentEvent.phase] : [];
    }

    return [];
  };

  const allowedPhases = getAllowedPhases(statusFilter);

  const getMergeScore = (p: ParticipantDTO) => {
    if (p.merged) return 1000;

    const correctedCount = p.gradingStatusDTOs.reduce((sum, gs) => {
      const perGroupCorrected = gs.gradingStatusPerGradingDetailsDTOs.filter(
        (g) => g.isCorrected,
      ).length;
      return sum + perGroupCorrected;
    }, 0);

    return correctedCount;
  };

  const stripDiacritics = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const term = stripDiacritics(searchTerm);

  const filteredParticipants = participants
    .filter((p) => {
      const stripped = {
        name: stripDiacritics(p.fullName),
        email: stripDiacritics(p.email),
        code: stripDiacritics(p.code ?? ""),
      };

      if (
        ![stripped.name, stripped.email, stripped.code].some((s) =>
          s.includes(term),
        )
      ) {
        return false;
      }

      if (teamFilter !== "ALL") {
        const teamId = Number(teamFilter);
        if (p.team?.id !== teamId) return false;
      }

      if (locationFilter !== "ALL") {
        if (p.location !== locationFilter) return false;
      }

      return true;
    })
    .sort((a, b) => {
      if (sortBy === "points") {
        const v1 = a.points ?? 0;
        const v2 = b.points ?? 0;
        return sortOrder === "asc" ? v1 - v2 : v2 - v1;
      }

      if (sortBy === "merged") {
        const scoreA = getMergeScore(a);
        const scoreB = getMergeScore(b);

        if (scoreA !== scoreB)
          return sortOrder === "asc" ? scoreA - scoreB : scoreB - scoreA;

        return a.fullName.localeCompare(b.fullName);
      }

      if (sortBy === "date") {
        const d1 = a.date ? new Date(a.date).getTime() : 0;
        const d2 = b.date ? new Date(b.date).getTime() : 0;

        return sortOrder === "asc" ? d1 - d2 : d2 - d1;
      }

      return 0;
    });

  const pagedParticipants = filteredParticipants.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const shouldShowGradingColumns =
    statusFilter === EventStatusFilter.ALL || statusFilter === undefined
      ? currentEvent?.phase === EventPhase.Grading
      : GRADING_STATUSES.includes(statusFilter);

  const closeAllMenus = () => {
    setAnchorEl(null);
    setSelectedParticipantId(null);

    setSubMenuAnchorEl(null);
    setSubMenuItems([]);
    setActiveRowId(null);
  };

  const renderMenuItems = () =>
    possibleMenuItems.map((item) => {
      if (item.children) {
        return (
          <MenuItem
            key={item.label}
            sx={{ fontFamily: "Verdana", fontSize: "13px" }}
            onMouseEnter={(e) => {
              setSubMenuAnchorEl(e.currentTarget);
              setSubMenuItems(item.children!);
            }}
          >
            {item.label} ▶
          </MenuItem>
        );
      }

      return (
        <MenuItem
          key={item.label}
          sx={{ fontFamily: "Verdana", fontSize: "13px" }}
          onClick={() => {
            handlePerformAction(
              selectedParticipantId!,
              item.action!,
              (item as any).locationId,
            );
            closeAllMenus();
          }}
        >
          {item.label}
        </MenuItem>
      );
    });

  return (
    <div style={{ padding: 10 }} ref={tableWrapperRef}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {selectedEvent ? (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <h2 style={{ marginBottom: 0, fontFamily: "Verdana" }}>
              Current Event: {selectedEvent.name}
            </h2>
            <h4 style={{ marginTop: 0, fontFamily: "Verdana" }}>
              Phase: {EventPhase[selectedEvent.phase]}
            </h4>
          </div>
        ) : (
          <p style={FONT_STYLE}>No enabled event.</p>
        )}

        <SpeedDial
          ariaLabel="quick actions"
          sx={{ position: "absolute", bottom: 16, right: 16 }}
          icon={<SpeedDialIcon />}
        >
          {actions.map((a) => (
            <SpeedDialAction
              key={a.name}
              icon={a.icon}
              tooltipTitle={a.name}
              onClick={() => handleSpeedDial(a.op)}
            />
          ))}
        </SpeedDial>
      </div>

      <div
        style={{
          display: "flex",
          gap: "1rem",
          marginTop: "1rem",
          marginBottom: "1rem",
        }}
      >
        <TextField
          label="Search (Name/Email/Code)"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={onSearchTermChange}
          style={{ width: "40%" }}
          InputProps={{
            style: { height: 35, padding: "0 14px", ...FONT_STYLE },
          }}
          InputLabelProps={{ style: FONT_STYLE }}
        />
        {currentEvent?.phase !== EventPhase.Test &&
          (currentEvent?.allLocations?.length ?? 0) > 1 && (
            <FormControl
              variant="outlined"
              style={{ minWidth: 200, width: "30%" }}
            >
              <InputLabel id="location-filter-label" sx={FONT_STYLE}>
                Filter by Location
              </InputLabel>
              <Select
                labelId="location-filter-label"
                label="Filter by Location"
                value={locationFilter}
                onChange={onLocationFilterChange}
                style={{ height: 35, ...FONT_STYLE }}
              >
                <MenuItem value="ALL" sx={FONT_STYLE}>
                  All
                </MenuItem>
                {currentEvent?.allLocations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.name} sx={FONT_STYLE}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        <FormControl variant="outlined" style={{ minWidth: 200, width: "30%" }}>
          <InputLabel id="status-filter-label" sx={FONT_STYLE}>
            Filter by Status
          </InputLabel>
          <Select
            labelId="status-filter-label"
            label="Filter by Status"
            value={statusFilter ?? EventStatusFilter.ALL}
            onChange={onStatusFilterChange}
            style={{ height: 35, ...FONT_STYLE }}
          >
            {(currentEvent?.phase
              ? PHASE_STATUS_VISIBILITY[currentEvent.phase]
              : [EventStatusFilter.ALL]
            ).map((status) => (
              <MenuItem key={status} value={status} sx={FONT_STYLE}>
                {EVENT_STATUS_LABELS[status]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {currentEvent?.phase !== EventPhase.Test && (
          <FormControl
            variant="outlined"
            style={{ minWidth: 200, width: "30%" }}
          >
            <InputLabel id="team-filter-label" sx={FONT_STYLE}>
              Filter by Team
            </InputLabel>
            <Select
              labelId="team-filter-label"
              label="Filter by Team"
              value={teamFilter}
              onChange={onTeamFilterChange}
              style={{ height: 35, ...FONT_STYLE }}
            >
              <MenuItem value="ALL" sx={FONT_STYLE}>
                All
              </MenuItem>
              {currentEvent?.teams
                .filter((team) =>
                  team.teamMembers.some(
                    (tm) => profile?.isAdmin || tm.id === profile?.id,
                  ),
                )
                .filter((team) => allowedPhases.includes(team.eventPhase))
                .map((team) => (
                  <MenuItem key={team.id} value={team.id} sx={FONT_STYLE}>
                    {team.nameWithMemberNames}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}

        <IconButton
          color="primary"
          aria-label="Refresh"
          onClick={() => refreshParticipants()}
        >
          <FaSync />
        </IconButton>
      </div>

      {filteredParticipants.length === 0 ? (
        <p style={FONT_STYLE}>
          No participants found (check your filters or search)
        </p>
      ) : (
        <TableContainer component={Paper} style={{ marginTop: "1rem" }}>
          <Table
            sx={{
              "& .MuiTableRow-root": { height: "40px !important" },
              "& .MuiTableCell-root": {
                padding: "0px 8px",
                lineHeight: 1,
                fontSize: "0.85rem",
                fontFamily: "Verdana",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={FONT_STYLE}>Full Name</TableCell>
                <TableCell sx={FONT_STYLE}>Email</TableCell>
                <TableCell sx={FONT_STYLE}>Faculty</TableCell>
                <TableCell align="center" sx={FONT_STYLE}>
                  Year
                </TableCell>
                <TableCell align="center" sx={FONT_STYLE}>
                  Phone
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    textAlign: "center",
                    verticalAlign: "middle",
                    ...FONT_STYLE,
                  }}
                >
                  Status
                </TableCell>
                {!INTERVIEW_STATUSES.includes(statusFilter as any) && (
                  <>
                    <TableCell sx={FONT_STYLE}>Last Login</TableCell>
                    <TableCell sx={FONT_STYLE}>Finished At</TableCell>
                  </>
                )}
                {profile?.isAdmin &&
                  currentEvent?.phase === EventPhase.Test && (
                    <TableCell sx={FONT_STYLE}>FP</TableCell>
                  )}
                {!INTERVIEW_STATUSES.includes(statusFilter as any) && (
                  <TableCell sx={FONT_STYLE}>Code</TableCell>
                )}
                {INTERVIEW_STATUSES.includes(statusFilter as any) && (
                  <TableCell
                    align="center"
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      cursor: "pointer",
                      userSelect: "none",
                      ...FONT_STYLE,
                    }}
                    onClick={() => handleSortColumn("date")}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      Date
                      {getSortIcon("date")}
                    </Box>
                  </TableCell>
                )}
                <TableCell sx={FONT_STYLE}>Location</TableCell>
                {(shouldShowGradingColumns ||
                  currentEvent?.phase === EventPhase.Test) && (
                  <TableCell sx={FONT_STYLE}>Link</TableCell>
                )}
                {currentEvent?.phase !== EventPhase.Test && (
                  <TableCell
                    align="center"
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      ...FONT_STYLE,
                    }}
                  >
                    Team
                  </TableCell>
                )}
                {(shouldShowGradingColumns ||
                  INTERVIEW_STATUSES.includes(statusFilter as any)) && (
                  <TableCell
                    align="center"
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      cursor: "pointer",
                      userSelect: "none",
                      ...FONT_STYLE,
                    }}
                    onClick={() => handleSortColumn("merged")}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      Grading
                      {getSortIcon("merged")}
                    </Box>
                  </TableCell>
                )}
                {INTERVIEW_STATUSES.includes(statusFilter as any) && (
                  <TableCell
                    align="center"
                    sx={{
                      textAlign: "center",
                      verticalAlign: "middle",
                      ...FONT_STYLE,
                    }}
                  >
                    Interview
                  </TableCell>
                )}
                {currentEvent?.phase !== EventPhase.Test && (
                  <TableCell
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      ...FONT_STYLE,
                    }}
                    onClick={() => handleSortColumn("points")}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      Points
                      {getSortIcon("points")}
                    </Box>
                  </TableCell>
                )}
                {profile?.isAdmin &&
                  currentEvent?.phase !== EventPhase.Closed && (
                    <TableCell sx={FONT_STYLE}>Actions</TableCell>
                  )}
              </TableRow>
            </TableHead>

            <TableBody>
              {pagedParticipants.map((p) => {
                const { label, color } = getStatusInfo(p.status);
                return (
                  <TableRow
                    key={p.id}
                    sx={{
                      backgroundColor:
                        activeRowId === p.id
                          ? "rgba(29, 98, 191, 0.12)"
                          : "transparent",
                      transition: "background-color 150ms ease",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <TableCell sx={FONT_STYLE}>{p.fullName}</TableCell>
                    <TableCell sx={FONT_STYLE}>{p.email}</TableCell>
                    <TableCell sx={FONT_STYLE}>{p.faculty}</TableCell>
                    <TableCell align="center" sx={FONT_STYLE}>
                      {p.yearOfStudy}
                    </TableCell>
                    <TableCell align="center" sx={FONT_STYLE}>
                      {p.phone}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ textAlign: "center", verticalAlign: "middle" }}
                    >
                      <Chip
                        label={label}
                        sx={{
                          backgroundColor: color,
                          color: "#000",
                          fontWeight: "bold",
                          ...FONT_STYLE,
                        }}
                      />
                    </TableCell>
                    {!INTERVIEW_STATUSES.includes(statusFilter as any) && (
                      <>
                        <TableCell sx={FONT_STYLE}>
                          {p.lastLogin
                            ? new Date(p.lastLogin).toLocaleTimeString([], {
                                hour12: false,
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              })
                            : ""}
                        </TableCell>

                        <TableCell sx={FONT_STYLE}>
                          {p.finishedAt
                            ? new Date(p.finishedAt).toLocaleTimeString([], {
                                hour12: false,
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              })
                            : ""}
                        </TableCell>
                      </>
                    )}
                    {profile?.isAdmin &&
                      currentEvent?.phase === EventPhase.Test && (
                        <TableCell>
                          {currentEvent?.status !== 3 &&
                            p.deviceFingerPrint != null &&
                            p.deviceFingerPrint != "" && (
                              <Tooltip
                                title={
                                  "Reset fingerprint: " + p.deviceFingerPrint
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handlePerformAction(p.id, 5)}
                                >
                                  <FaFingerprint />
                                </IconButton>
                              </Tooltip>
                            )}
                          {currentEvent?.status === 3 &&
                            currentEvent?.phase === EventPhase.Test &&
                            p.deviceFingerPrint != null &&
                            p.deviceFingerPrint != "" && (
                              <Tooltip
                                title={"Fingerprint: " + p.deviceFingerPrint}
                              >
                                <IconButton size="small">
                                  <FaFingerprint />
                                </IconButton>
                              </Tooltip>
                            )}
                        </TableCell>
                      )}
                    {!INTERVIEW_STATUSES.includes(statusFilter as any) && (
                      <TableCell sx={FONT_STYLE}>{p.code || ""}</TableCell>
                    )}
                    {INTERVIEW_STATUSES.includes(statusFilter as any) && (
                      <TableCell align="center" sx={FONT_STYLE}>
                        {p.date
                          ? new Date(p.date).toLocaleString("en-GB", {
                              day: "numeric",
                              month: "long",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })
                          : ""}
                      </TableCell>
                    )}
                    <TableCell sx={FONT_STYLE}>{p.location || ""}</TableCell>
                    {(shouldShowGradingColumns ||
                      currentEvent?.phase === EventPhase.Test) && (
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => {
                            activateRow(p.id);
                            handleParticipantLinkClick(p.linkKey);
                          }}
                        >
                          <FaLink
                            style={{ color: p.hasRemarks ? "#1d62bf" : "gray" }}
                          />
                        </IconButton>
                      </TableCell>
                    )}
                    {currentEvent?.phase !== EventPhase.Test && (
                      <TableCell align="center" sx={FONT_STYLE}>
                        {p.team?.name}
                      </TableCell>
                    )}
                    {((shouldShowGradingColumns && p.merged) ||
                      INTERVIEW_STATUSES.includes(statusFilter as any)) && (
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            activateRow(p.id);
                            handleMergeCompletionClick(p.linkKey);
                          }}
                        >
                          <FaCheck color={"#1d62bf"} style={{ fontSize: 20 }} />
                        </IconButton>
                      </TableCell>
                    )}
                    {shouldShowGradingColumns && !p.merged && (
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {p.gradingStatusDTOs.map((gs, index) => (
                            <Tooltip
                              key={index}
                              title={
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 0.5,
                                    padding: "2px 10px",
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "bold", ...FONT_STYLE }}
                                  >
                                    {gs.teamMemberName}
                                  </Typography>
                                  <Box sx={{ display: "flex", gap: 1.0 }}>
                                    {gs.gradingStatusPerGradingDetailsDTOs.map(
                                      (grading, i) => (
                                        <FaCheck
                                          key={i}
                                          color={
                                            grading.isCorrected
                                              ? "#1d62bf"
                                              : "gray"
                                          }
                                          style={{ fontSize: 20 }}
                                        />
                                      ),
                                    )}
                                  </Box>
                                </Box>
                              }
                              placement="top"
                              arrow
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: "#fff",
                                    color: "#000",
                                    boxShadow: 3,
                                    borderRadius: 2,
                                  },
                                },
                                arrow: { sx: { color: "#fff" } },
                              }}
                            >
                              <IconButton
                                size="small"
                                sx={{ padding: 0.5 }}
                                style={{
                                  color:
                                    gradingStatusColorMap[gs.gradingStatus],
                                }}
                              >
                                <FaUserAlt />
                              </IconButton>
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                    )}
                    {INTERVIEW_STATUSES.includes(statusFilter as any) && (
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (
                              p.interviewStatus === InterviewStatus.Done ||
                              p.interviewStatus ===
                                InterviewStatus.DoneWithCorrection
                            ) {
                              activateRow(p.id);

                              window.open(
                                `/code-viewer?linkKey=${encodeURIComponent(p.linkKey)}&merge=${MergeModeEnum.Interview}`,
                                "_blank",
                              );
                            }
                          }}
                          disabled={
                            !(
                              p.interviewStatus === InterviewStatus.Done ||
                              p.interviewStatus ===
                                InterviewStatus.DoneWithCorrection
                            )
                          }
                        >
                          <FaCheck
                            style={{
                              fontSize: 18,
                              color: interviewStatusColorMap[p.interviewStatus],
                            }}
                          />
                        </IconButton>
                      </TableCell>
                    )}

                    {currentEvent?.phase !== EventPhase.Test && (
                      <TableCell sx={FONT_STYLE}>
                        {p.points.toFixed(2)}
                      </TableCell>
                    )}
                    <TableCell>
                      {profile?.isAdmin &&
                        currentEvent?.phase !== EventPhase.Closed && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, p.id)}
                          >
                            <FaEllipsisH />
                          </IconButton>
                        )}
                      {profile?.isAdmin &&
                        currentEvent?.phase !== EventPhase.Closed && (
                          <div>
                            <Menu
                              anchorEl={anchorEl}
                              open={Boolean(anchorEl)}
                              onClose={closeAllMenus}
                            >
                              {renderMenuItems()}
                            </Menu>

                            <Menu
                              anchorEl={subMenuAnchorEl}
                              open={Boolean(subMenuAnchorEl)}
                              onClose={closeAllMenus}
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                              }}
                            >
                              {subMenuItems.map((child) => (
                                <MenuItem
                                  key={child.label}
                                  sx={{
                                    fontFamily: "Verdana",
                                    fontSize: "13px",
                                  }}
                                  onClick={() => {
                                    handlePerformAction(
                                      selectedParticipantId!,
                                      child.action!,
                                      (child as any).locationId,
                                      child.teamId,
                                    );
                                    closeAllMenus();
                                  }}
                                >
                                  {child.label}
                                </MenuItem>
                              ))}
                            </Menu>
                          </div>
                        )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filteredParticipants.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-displayedRows": FONT_STYLE,
              "& .MuiTablePagination-selectLabel": FONT_STYLE,
              "& .MuiTablePagination-select": FONT_STYLE,
            }}
          />
        </TableContainer>
      )}

      <Dialog
        open={assignTeamDialogOpen}
        onClose={() => setAssignTeamDialogOpen(false)}
      >
        <DialogTitle sx={FONT_STYLE}>Assign Teams to Participants</DialogTitle>
        <DialogContent>
          <Typography sx={FONT_STYLE}>
            Are you sure you want to automatically assign teams to all
            participants for event <strong>{currentEvent?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAssignTeamDialogOpen(false)}
            sx={FONT_STYLE}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignTeams}
            variant="contained"
            color="primary"
            sx={FONT_STYLE}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={addTeamMembersDialogOpen}
        onClose={() => setAddTeamMembersDialogOpen(false)}
      >
        <DialogTitle sx={FONT_STYLE}>Add Team Members</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 400,
            paddingTop: "12px !important",
          }}
        >
          <Typography sx={FONT_STYLE}>
            Upload a JSON file containing the team members to import for event{" "}
            <strong>{currentEvent?.name}</strong>.
          </Typography>
          <Typography sx={{ ...FONT_STYLE, color: "gray", fontSize: "11px" }}>
            {`Expected format:
              {
                eventPhaseId: number,
                teamMembers: [
                  {
                    teamName: string,
                    fullName: string,
                    userName: string,
                    isAdmin: boolean,
                    unassignFromTeam?: boolean (default: false)
                  }
                ]
              }`}
          </Typography>
          <Button variant="outlined" component="label" sx={FONT_STYLE}>
            {teamMembersFile ? teamMembersFile.name : "Choose JSON File"}
            <input
              type="file"
              accept=".json"
              hidden
              onChange={(e) => setTeamMembersFile(e.target.files?.[0] ?? null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddTeamMembersDialogOpen(false);
              setTeamMembersFile(null);
            }}
            sx={FONT_STYLE}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddTeamMembers}
            variant="contained"
            color="primary"
            disabled={!teamMembersFile}
            sx={FONT_STYLE}
          >
            Upload & Import
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={acceptParticipantsDialogOpen}
        onClose={() => setAcceptParticipantsDialogOpen(false)}
      >
        <DialogTitle sx={FONT_STYLE}>Accept Participants</DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 360,
            paddingTop: "12px !important",
          }}
        >
          <Typography sx={FONT_STYLE}>
            Accept all participants for event{" "}
            <strong>{currentEvent?.name}</strong> who have finished and meet the
            minimum points threshold.
          </Typography>
          {(currentEvent?.allLocations?.length ?? 0) > 1 && (
            <FormControl variant="outlined" size="small">
              <InputLabel sx={FONT_STYLE}>Location</InputLabel>
              <Select
                value={selectedLocationId ?? ""}
                onChange={(e) => setSelectedLocationId(Number(e.target.value))}
                label="Location"
                sx={FONT_STYLE}
              >
                {currentEvent?.allLocations?.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id} sx={FONT_STYLE}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            label="Minimum Points"
            type="number"
            variant="outlined"
            size="small"
            value={minPoints}
            onChange={(e) =>
              setMinPoints(e.target.value ? Number(e.target.value) : null)
            }
            InputProps={{ style: FONT_STYLE }}
            InputLabelProps={{ style: FONT_STYLE }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAcceptParticipantsDialogOpen(false)}
            sx={FONT_STYLE}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAcceptParticipants}
            variant="contained"
            color="primary"
            disabled={!selectedLocationId}
            sx={FONT_STYLE}
          >
            Accept Participants
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Dashboard;
